import useMe from '@/renderer/utils/useMe';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';
import type { RequestSchema } from '@/types/request';
import type React from 'react';
import { type ReactNode, createContext, useContext, useMemo, useReducer, useRef } from 'react';

// Define the interfaces
interface UserActivity {
  name: string;
  lastActivity: number;
  status: 'TYPE' | 'VIEW' | 'LEFT' | 'IDLE';
  typeLength?: number;
}

interface ByChatId {
  [chatId: API.Id]:
    | {
        users: {
          [userId: API.Id]: UserActivity | undefined;
        };
      }
    | undefined;
}

interface ByUserId {
  [userId: API.Id]: number; // chatId where the user is now
}

interface TypingState {
  byChatId: ByChatId;
  byUserId: ByUserId;
}

interface UpdateAction {
  type: 'UPDATE';
  name: string;
  userId: API.Id;
  chatId: API.Id;
  status: 'TYPE' | 'VIEW' | 'LEFT' | 'IDLE';
  typeLength?: number;
}

export const typingRequest: RequestSchema<
  API.DirectMessageReceipt,
  API.WithData<{ type: 'dm'; len?: number | null }>
> = {
  name: 'messageCreateRequest',
  input: '/api/v1/messageReceipt/realtime/typing/:chatId',
  method: 'POST',
};

// Reducer function
const typingReducer = (state: TypingState, action: UpdateAction): TypingState => {
  const { name, userId, chatId, status, typeLength } = action;
  const now = Date.now();

  let changed = false;

  if (status === 'TYPE' || status === 'VIEW') {
    // User is now typing or actively viewing a chat
    if (state.byUserId[userId] !== chatId) {
      // User has moved to a different chat, so we have to modify the old one first
      const oldChatId = state.byUserId[userId];

      if (oldChatId && state.byChatId[oldChatId]) {
        const old = state.byChatId[oldChatId].users[userId];

        if (old && (old?.lastActivity < now - 500 || old?.status !== 'LEFT')) {
          changed = true;

          state.byChatId[oldChatId].users[userId] = {
            ...old,
            lastActivity: now,
            status: 'LEFT',
          };
        }
      }

      changed = true;

      // And now update the currently active chat for this user
      state.byUserId[userId] = chatId;
    }
  }

  if (!state.byChatId[chatId]) {
    state.byChatId[chatId] = { users: {} };
  }

  const old = state.byChatId[chatId].users[userId];

  if (
    !old ||
    ((old.lastActivity < now - 500 || old.status !== status || old.typeLength !== typeLength) ?? 0)
  ) {
    changed = true;

    state.byChatId[chatId].users[userId] = {
      name,
      lastActivity: now,
      status,
      typeLength: typeLength ?? 0,
    };
  }

  if (!changed) {
    // Return same object if nothing changed
    return state;
  }

  // Nested new objection creation
  return {
    ...state,
    byChatId: {
      ...state.byChatId,
      [chatId]: {
        ...state.byChatId[chatId],
        users: {
          ...state.byChatId[chatId]?.users,
        },
      },
    },
    byUserId: {
      ...state.byUserId,
      [userId]: chatId,
    },
  };
};

// Create the context
const TypingContext = createContext<
  { state: TypingState; dispatch: React.Dispatch<UpdateAction> } | undefined
>(undefined);

// Context provider component
export const TypingProvider = ({ children }: { children: ReactNode }) => {
  // Initial state
  const initialState: TypingState = {
    byChatId: {},
    byUserId: {},
  };

  const [state, dispatch] = useReducer(typingReducer, initialState);

  return <TypingContext.Provider value={{ state, dispatch }}>{children}</TypingContext.Provider>;
};

// Custom hooks to use context
export const useTypingContext = () => {
  const context = useContext(TypingContext);
  if (context === undefined) {
    throw new Error('useTypingContext must be used within a TypingProvider');
  }
  return context;
};

export const TypingStateUpdater = () => {
  const { dispatch } = useTypingContext();
  const timeouts = useRef({} as Record<`${API.Id}.${API.Id}`, ReturnType<typeof setTimeout>>);
  const role = useMe()?.role;

  const onDmTyping = useMemo(() => {
    return async (
      _event: MessageEvent,
      ojson: API.WebsocketResult<{
        name: string;
        userId: API.Id;
        len?: number;
        chatId: API.Id;
      }>,
    ) => {
      if (role !== 'admin' && role !== 'staff') {
        return;
      }

      const typeLength = ojson.data.len;
      const tkey = `${ojson.data.userId}.${ojson.data.chatId}` as const;

      if (timeouts.current[tkey]) {
        clearTimeout(timeouts.current[tkey]);
      }

      const nextStatus = typeLength === undefined ? 'LEFT' : typeLength > 0 ? 'TYPE' : 'VIEW';

      if (nextStatus !== 'LEFT') {
        timeouts.current[tkey] = setTimeout(
          () =>
            dispatch({
              type: 'UPDATE',
              name: ojson.data.name || `#${ojson.data.userId}`,
              userId: ojson.data.userId,
              chatId: ojson.data.chatId,
              // after 10s, change status to viewing not typing
              status: nextStatus === 'TYPE' ? 'VIEW' : 'IDLE',
            }),
          // after 60s, change status to idle, not viewing
          nextStatus === 'TYPE' ? 10000 : 60000,
        );
      }

      dispatch({
        type: 'UPDATE',
        name: ojson.data.name || `#${ojson.data.userId}`,
        userId: ojson.data.userId,
        chatId: ojson.data.chatId,
        status: nextStatus,
        typeLength,
      });
    };
  }, [dispatch, role]);

  const onDmReceived = useMemo(() => {
    return async (_event: MessageEvent, ojson: API.WebsocketResult<API.DirectMessageReceipt>) => {
      if (role !== 'admin' && role !== 'staff') {
        return;
      }

      const chatId = ojson.data.receiverUserId ? ojson.data.receiverUserId : ojson.data.userId;
      const userId = ojson.data.userId;
      const name = ojson.data.user?.firstName;
      const tkey = `${userId}.${chatId}` as const;

      if (timeouts.current[tkey]) {
        clearTimeout(timeouts.current[tkey]);
      }

      timeouts.current[tkey] = setTimeout(
        () =>
          dispatch({
            type: 'UPDATE',
            name: name || `#${userId}`,
            userId: userId,
            chatId: chatId,
            status: 'IDLE',
          }),
        60000,
      );

      dispatch({
        type: 'UPDATE',
        name: name || `#${userId}`,
        userId: userId,
        chatId: chatId,
        status: 'VIEW',
        typeLength: 0,
      });
    };
  }, [dispatch, role]);

  useWebSocket(WS_BASE_URL, {
    connect: role === 'admin' || role === 'staff',
    subscriptions: {
      'directMessageReceipt/created': onDmReceived,
      'directMessageReceipt/typing': onDmTyping,
    },
  });

  return null;
};
